/**
 * GET /v1/sales/forecast/team — the Command Center Team Snapshot.
 * The response type lives in @waypoint/shared so the API's return and this
 * reader cannot drift again (the widget once read rep_name / closed_cents /
 * team_pipeline_cents and showed $0k and nameless reps at 0% for everyone).
 */
import { api } from '@/lib/api';
import type { TeamForecastResponse } from '@waypoint/shared';

export type { TeamForecastResponse, TeamForecastRep } from '@waypoint/shared';

export function getTeamForecast(): Promise<TeamForecastResponse> {
  return api<TeamForecastResponse>('/v1/sales/forecast/team');
}

export interface TeamSnapshotRow {
  key: string;
  /** users.full_name, else the login email. */
  label: string;
  pipelineCents: number;
  closedCents: number;
  /** null when the rep has no target for the period — no bar, no 0%. */
  quotaAttainment: number | null;
}

/**
 * Reps by open pipeline, largest first. Commit / best case are left out: the
 * API derives them from HubSpot probability, which is empty for every other CRM.
 */
export function teamSnapshotRows(data: TeamForecastResponse, limit = 5): TeamSnapshotRow[] {
  return [...(data.reps ?? [])]
    .sort((a, b) => b.pipelineCents - a.pipelineCents || b.closedCents - a.closedCents)
    .slice(0, limit)
    .map(r => ({
      key: r.userId,
      label: r.name || r.email || 'Unnamed member',
      pipelineCents: r.pipelineCents,
      closedCents: r.closedCents,
      quotaAttainment: r.targetCents > 0 ? r.quotaAttainment : null,
    }));
}
